<template>
  <div ref="root" class="container relative mt-7.5">
    <div class="mb-5 flex gap-x-7.5">
      <div class="w-full">
        <div class="mb-4 flex items-center gap-2.5 md:gap-9">
          <UiButton
            variant="clear"
            class="border-gray-60 h-9.5 border px-2.5 text-xs uppercase"
            content-class="flex items-center gap-x-1.5"
            @click="filterDrawer = true"
          >
            <span class="hidden md:inline-block">
              {{ $t("showFilters") }} {{ appliedFiltersCount }}
            </span>
            <span class="inline-block md:hidden">
              {{ $t("filters") }} {{ appliedFiltersCount }}
            </span>
            <UiIcon name="filter" class="size-6 shrink-0" />
          </UiButton>
          <p
            v-if="catalogData"
            class="order-1 text-xs uppercase md:order-3 md:font-bold"
          >
            {{ $t("amountProducts", { count: catalogData?.meta?.total || 0 }) }}
          </p>
          <div class="order-2 ml-auto flex items-center md:gap-9">
            <UiGridDesktopToggle
              v-model="gridSize.desktop"
              class="max-md:hidden"
            />
            <UiGridMobileToggle v-model="gridSize.mobile" class="md:hidden" />
            <UiFilterUiSort
              class="max-md:hidden"
              btn-classes="font-normal text-text-secondary font-semibold truncate text-sm max-lg2:px-2.5 max-lg2:h-9.5 max-lg2:border max-lg2:border-gray-border max-lg2:w-full"
              @onChangeSort="onChangeSort"
            />
          </div>
        </div>
        <UiFilterDrawer
          v-model="filterDrawer"
          :applied-filters-count="appliedFiltersCount"
          :products-count="catalogData?.meta?.total || 0"
        >
          <UiFilterTags
            v-if="filterTags && !isEmpty(filterTags.tags)"
            :filter-tags="filterTags"
            chips
            class="md:order-0 border-gray-60 md:bg-off-white-10 order-1 px-4 py-6 md:border-b md:px-18"
          />
          <UiFilter
            v-if="!pendingMinMax"
            :collection-ids="collectionIds"
            class="order-0 w-full md:order-1"
            :hide-filter-type="
              plpType === plpTypes.engagementRing && $route.query.dsku
                ? ['DIAMOND_SHAPE']
                : undefined
            "
            :category-ids="categoryIds"
            :show-all="data.enable_all_filter"
            :show_categories_filters="data?.show_categories_filters"
            :show_collections_filters="data?.show_collections_filters"
            :min-max-data="minMaxDataRaw?.data"
            block-classes="py-5 border-b border-gray-60 px-4 md:px-18"
            @onChangeSort="onChangeSort"
          />
          <div class="order-3 space-y-5 px-4 py-5 text-center md:px-18">
            <UiButton
              class="w-full uppercase"
              size="md"
              @click="filterDrawer = false"
            >
              {{ $t("close") }}
            </UiButton>
            <UiButton
              variant="text"
              class="h-auto px-0 py-1 text-sm uppercase underline underline-offset-4"
              data-e2e-cta="reset-filters"
              @click="filterTags.removeTagAll"
            >
              {{ $t("clearAllFilters") }}
            </UiButton>
          </div>
        </UiFilterDrawer>
        <div
          v-if="pending && (!infiniteScroll || !dataCatalog?.length)"
          class="mb-40 flex w-full items-start justify-center bg-white py-28"
        >
          <img
            src="/images/loader.gif"
            alt="Loader"
            class="h-50 w-50 object-contain grayscale"
          />
        </div>
        <template v-else>
          <div ref="targetElTop" class="h-px"></div>
          <img
            v-if="prevDataLoading && infiniteScroll"
            src="/images/loader.gif"
            alt="Loader"
            class="absolute left-1/2 top-0 size-8 -translate-x-1/2 object-contain grayscale"
          />
          <div
            v-if="isEmpty(dataCatalog)"
            class="min min-h-[424px] text-center"
          >
            {{ $t("weDontHaveAnyProductsThatFitYourCurrentSearch") }}
          </div>
          <BlocksBlockProductsWithBanners
            v-else
            :data-catalog="dataCatalog || []"
            :page-data="pageData"
            :plp-type="plpType"
            :grid-size-desktop="gridSize.desktop"
            :grid-size-mobile="gridSize.mobile"
            :sort-by="sortBy"
          />
          <div
            v-if="!isEmpty(dataCatalog) && catalogData?.meta?.last_page > 1"
            class="relative flex justify-center py-7.5"
          >
            <UiPagination
              v-model="page"
              :total-pages="catalogData?.meta?.last_page || 0"
              :class="pending && infiniteScroll ? 'opacity-0' : ''"
              @update:model-value="changePage"
            />
            <img
              v-if="pending && infiniteScroll"
              src="/images/loader.gif"
              alt="Loader"
              class="absolute left-1/2 top-0 size-8 -translate-x-1/2 object-contain grayscale"
            />
          </div>
          <div v-if="!infiniteScroll" ref="targetEl" class="h-px"></div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMounted, useStorage } from "@vueuse/core";
import { hash } from "ohash";
import { BlockPlpCatalog, CatalogData, PageData } from "ecom-types";

import { GetCatalogParams } from "~/api";
import { plpTypes } from "@/configs";
import UiGridMobileToggle from "~/components/Ui/grid/UiGridMobileToggle.vue";

const props = defineProps<{
  data: BlockPlpCatalog;
  plpType?: string;
  pageData: PageData | null;
}>();

const { t } = useI18n();

const { $api } = useNuxtApp();
const { setPLPPageLdSchema } = useJsonLdSchema();

const { allFilters } = useGeneralStore();

const gridSize = useStorage("plp-grid", { mobile: 2, desktop: 4 }, undefined, {
  initOnMounted: true,
});

const route = useRoute();
const router = useRouter();
const targetEl = ref(null);
const filterDrawer = ref(false);
const prevDataLoading = ref(false); // prevPending work incorrect with immediate FALSE
const isMounted = useMounted();

if (route.query.dShape) {
  const materials = route.query.materials
    ? Array.isArray(route.query.materials)
      ? route.query.materials
      : [route.query.materials]
    : [];

  if (route.query.dShape && !materials.includes(route.query.dShape as string)) {
    materials.push(route.query.dShape as string);
  }

  await router.replace({ query: { ...route.query, materials } });
}

const minMaxKey = computed(() => hash(["catalogRange", props.pageData?.slug]));
const { data: minMaxDataRaw, pending: pendingMinMax } =
  await useAsyncCachedData(minMaxKey, () => {
    return $api.products.getCatalogRanges(
      objectToQuery({
        categories_sku: props.data.categories.map((m) => m.sku as string),
        collections: props.data.collections.map((m) => m.id),
        all: 1,
      }),
    );
  });

const { dynamicParamsEvent, viewItemListEvent } = useEvents();

const root = ref<HTMLElement | null>(null);
const collectionIds = computed(() => {
  return props.data?.collections?.map((m) => m.id);
});

const categoryIds = computed(() => {
  return props.data?.categories?.map((m) => m.id);
});

const { page, filterParams, sortBy, filterTags, prevPage } = useCatalogFilters({
  minMaxData: minMaxDataRaw.value?.data,
  collectionIds: collectionIds.value,
  categoryIds: categoryIds.value,
  showAll: props.data.enable_all_filter,
  showCategoriesFilters: props.data?.show_categories_filters,
  showCollectionsFilters: props.data?.show_collections_filters,
});

const filter = computed(() => {
  const tempFilter: GetCatalogParams = {
    ...filterParams.value,
    categories_sku: isEmpty(filterParams.value.categories_sku)
      ? props.data.categories.map((m) => m.sku as string)
      : filterParams.value.categories_sku,
    collections: isEmpty(filterParams.value.collections)
      ? props.data.collections.map((m) => m.id)
      : filterParams.value.collections,
    materials_sku: isEmpty(filterParams.value.materials_sku)
      ? props.data.materials
          .map((m) => m.sku)
          .filter((sku) => {
            return allFilters(
              collectionIds.value,
              categoryIds.value,
            )?.materials?.find((i) => i.sku === sku);
          })
      : filterParams.value.materials_sku,
  };

  return objectToQuery(tempFilter);
});

const appliedFiltersCount = computed(() => {
  const count = filterTags.value.tags.length;
  return count ? `(${count})` : "";
});

const debouncedFilter = refDebounced(filter, 300);
const prevFilter = computed(() => ({ ...filter.value, page: prevPage.value }));
const key = computed(() =>
  hash([`catalog-${props.pageData?.slug}`, unref(debouncedFilter.value)]),
);
const { data: catalogData, pending } = await useAsyncCachedData(
  key,
  () => {
    return $api.products.getCatalog(debouncedFilter);
  },
  {
    watch: [debouncedFilter],
  },
);

const prevKey = computed(() =>
  hash([`catalog-${props.pageData?.slug}`, unref(prevFilter.value)]),
);
const { data: prevCatalogData, execute } = await useAsyncCachedData(
  prevKey,
  () => {
    return $api.products.getCatalog(prevFilter);
  },
  {
    immediate: false,
  },
);

const dataCatalog = ref(catalogData.value?.data);
const infiniteScroll = ref(false);
const analyticsEventSent = ref(false);
const analyticsEventRetriggered = ref(false);
const initialPage = ref(1);
const ANALYTICS_RETRIGGER_DELAY_MS = 30000;
let analyticsTimeoutId: ReturnType<typeof setTimeout> | null = null;

function sendCatalogAnalyticsEvents() {
  if (!dataCatalog.value?.length) return;

  viewItemListEvent(dataCatalog.value, {
    item_list_name: props.pageData?.currentTranslation?.title || t("catalog"),
  });
  dynamicParamsEvent(`View Category`, {
    ecomm_prodid: dataCatalog.value.map((item) => item.default_sku),
    ecomm_pagetype: "category",
    ecomm_totalvalue: "",
    ecomm_category: props.pageData?.currentTranslation?.title || t("catalog"),
  });
}

function scheduleAnalyticsRetrigger() {
  if (analyticsTimeoutId) {
    clearTimeout(analyticsTimeoutId);
  }

  analyticsTimeoutId = setTimeout(() => {
    if (!analyticsEventRetriggered.value) {
      sendCatalogAnalyticsEvents();
      analyticsEventRetriggered.value = true;
    }
  }, ANALYTICS_RETRIGGER_DELAY_MS);
}

function checkPageProgressRetrigger() {
  if (analyticsEventRetriggered.value) return;

  const lastPage = catalogData.value?.meta?.last_page;
  if (!lastPage || lastPage <= 3) return;

  const currentPage = page.value;
  const halfPages = Math.ceil(lastPage / 2);

  if (initialPage.value === 1 && currentPage >= halfPages) {
    if (analyticsTimeoutId) {
      clearTimeout(analyticsTimeoutId);
      analyticsTimeoutId = null;
    }
    sendCatalogAnalyticsEvents();
    analyticsEventRetriggered.value = true;
  }
}

watchEffect(() => {
  if (
    catalogData.value?.meta?.last_page &&
    page.value > catalogData.value.meta.last_page
  ) {
    page.value = catalogData.value.meta.last_page;
  }
});

watch(pending, (value) => {
  if (!value) {
    if (infiniteScroll.value) {
      dataCatalog.value = [
        ...(dataCatalog.value || []),
        ...(catalogData.value?.data || []),
      ];
      infiniteScroll.value = false;
    } else {
      dataCatalog.value = catalogData.value?.data;
    }
  }
});

watch(
  [isMounted, dataCatalog],
  ([mounted, catalog]) => {
    if (mounted && catalog?.length && !analyticsEventSent.value) {
      initialPage.value = page.value;
      sendCatalogAnalyticsEvents();
      analyticsEventSent.value = true;
      scheduleAnalyticsRetrigger();
    }
  },
  { immediate: true },
);

watch(page, () => {
  checkPageProgressRetrigger();
});

// scroll top on search value change
watch(
  [() => filter.value.search, () => filter.value.page],
  ([searchVal, pageVal]) => {
    if (searchVal && isMounted.value && pageVal === 1) {
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
    }
  },
);

function changePage() {
  infiniteScroll.value = true;
  if (root.value) {
    root.value.scrollIntoView({ block: "start", behavior: "smooth" });
  }
  dataCatalog.value = [];
}

function onChangeSort(e) {
  sortBy.value = e;
}

const {
  observeLoadMoreTopObserver,
  unObserveLoadMoreTopObserver,
  targetElTop,
} = useTopObserver();
onMounted(() => {
  setTimeout(() => {
    useInfiniteScroll(
      targetEl,
      () => {
        if (
          catalogData.value?.meta?.last_page &&
          catalogData.value.meta.last_page > page.value &&
          !pending.value &&
          !infiniteScroll.value
        ) {
          infiniteScroll.value = true;
          page.value++;
        }
      },
      { distance: 1000, interval: 300 },
    );

    observeLoadMoreTopObserver();
  }, 0);
});

onBeforeUnmount(() => {
  unObserveLoadMoreTopObserver();
  if (analyticsTimeoutId) {
    clearTimeout(analyticsTimeoutId);
  }
});

setJSONLdSchema();

const paginationRelLinks = usePaginationRelLinks(
  page.value,
  catalogData.value?.meta?.last_pages || 1,
);

useHead({
  link: paginationRelLinks,
});

function setJSONLdSchema() {
  const name = props.pageData?.currentTranslation?.title || "";
  const image =
    getImageByPosition(props.pageData?.medias || [], "open-graph")?.file?.url ||
    getImageByPosition(props.pageData?.medias || [], "page")?.file?.url ||
    "";
  const numberOfItems = computed(() => catalogData.value?.meta?.total || 0);

  setPLPPageLdSchema({
    name,
    image,
    products: dataCatalog as Ref<CatalogData[]>,
    numberOfItems,
  });
}

function useTopObserver() {
  const loadMoreTopObserver = ref<IntersectionObserver | null>(null);
  const targetElTop = ref(null);

  async function loadMoreTopCallBack(entries) {
    if (
      catalogData.value?.meta?.last_page &&
      !pending.value &&
      !infiniteScroll.value &&
      dataCatalog.value?.length &&
      !prevDataLoading.value &&
      entries?.[0]?.isIntersecting
    ) {
      prevPage.value =
        filter.value.page -
        Math.ceil(dataCatalog.value.length / filter.value.count);

      if (!prevPage.value) {
        return;
      }
      infiniteScroll.value = true;
      prevDataLoading.value = true;
      await execute();

      const scrollHeight = window.document.documentElement.scrollHeight;
      const scrollTop = window.document.documentElement.scrollTop;

      dataCatalog.value = [
        ...(prevCatalogData.value?.data || []),
        ...(dataCatalog.value || []),
      ];
      await nextTick();

      const newScrollHeight = window.document.documentElement.scrollHeight;
      window.document.documentElement.scrollTop =
        newScrollHeight - scrollHeight + scrollTop;

      setTimeout(() => {
        prevDataLoading.value = false;
        infiniteScroll.value = false;
      }, 50);
    }
  }

  function observeLoadMoreTopObserver() {
    loadMoreTopObserver.value = new IntersectionObserver(loadMoreTopCallBack, {
      rootMargin: "400px 0px 0px 0px",
    });
    if (loadMoreTopObserver.value && targetElTop.value) {
      loadMoreTopObserver.value.observe(targetElTop.value);
    }
  }
  function unObserveLoadMoreTopObserver() {
    if (loadMoreTopObserver.value) {
      loadMoreTopObserver.value.disconnect();
    }
  }
  return {
    observeLoadMoreTopObserver,
    unObserveLoadMoreTopObserver,
    targetElTop,
  };
}
</script>
